import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import Handsontable from 'handsontable';
import { BaseRenderer } from 'handsontable/renderers/base';
import type Core from 'handsontable/core';
import { CellValue } from 'handsontable/common';
import { parseScoringScale } from '@/utils/observationTypeValidation';
import styles from './ScoringScaleEditor.module.scss';
import { _notNil } from '@/littledash';
import { ScoringScaleModal, isValidScoringScaleValue } from './ScoringScaleModal';

export const scoringScaleRenderer: BaseRenderer = (hotInstance, td, row, col, prop, value, cellProperties) => {
  // Clear existing content
  td.innerHTML = '';

  // TD styling must use inline styles to override Handsontable's base styles
  td.style.padding = '0';
  td.style.verticalAlign = 'middle';

  // Remove the htInvalid class that might be incorrectly applied
  td.classList.remove('htInvalid');

  // Only mark as invalid if there's an actual validation error and the cell has content
  const cellMeta = hotInstance.getCellMeta(row, col);
  if (cellMeta.valid === false && _notNil(value) && value !== '') {
    td.classList.add('htInvalid');
  }

  const isReadOnly = cellProperties.readOnly === true;

  // Create a wrapper div that takes full cell dimensions
  const wrapper = document.createElement('div');
  wrapper.className = isReadOnly ? `${styles.scoringScaleCell} ${styles.readOnly}` : styles.scoringScaleCell;

  const scaleValueDiv = document.createElement('div');
  scaleValueDiv.className = styles.scaleValue;

  if (!_notNil(value) || value === '') {
    // Only show placeholder if not read-only
    if (!isReadOnly) {
      const placeholder = document.createElement('span');
      placeholder.className = styles.placeholder;
      placeholder.textContent = 'Click to set scale';
      scaleValueDiv.appendChild(placeholder);
    }
  } else {
    const badge = document.createElement('div');
    badge.className = styles.scaleBadge;

    const badgeText = document.createElement('span');
    badgeText.className = styles.badgeText;
    badgeText.textContent = String(value);
    badge.appendChild(badgeText);

    // Only show clear button if not read-only
    if (!isReadOnly) {
      const clearButton = document.createElement('button');
      clearButton.className = styles.badgeClearButton;
      clearButton.innerHTML = '×';
      clearButton.setAttribute('aria-label', 'Clear scale');
      clearButton.onclick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        hotInstance.setDataAtCell(row, col, '');
      };
      badge.appendChild(clearButton);
    }

    scaleValueDiv.appendChild(badge);
  }

  wrapper.appendChild(scaleValueDiv);
  td.appendChild(wrapper);
};

export class ScoringScaleEditor extends Handsontable.editors.TextEditor {
  static type = 'scoring-scale';

  static register(cellType = ScoringScaleEditor.type) {
    Handsontable.cellTypes.registerCellType(cellType, {
      editor: ScoringScaleEditor,
      renderer: scoringScaleRenderer,
      validator: (value: CellValue, callback: (valid: boolean) => void) => {
        // Validate that the value is either empty or a parseable scoring scale format
        callback(isValidScoringScaleValue(value));
      },
    });
  }

  #modalContainer: HTMLDivElement | null = null;
  #modalOverlay: HTMLDivElement | null = null;
  #root: ReactDOM.Root | null = null;
  #currentValue: string = '';

  constructor(hotInstance: Core) {
    super(hotInstance);
    this.#modalContainer = null;
    this.#modalOverlay = null;
    this.#root = null;
    this.#currentValue = '';
  }

  prepare(
    row: number,
    col: number,
    prop: string | number,
    td: HTMLTableCellElement,
    value: string,
    cellProperties: Handsontable.CellProperties
  ): void {
    super.prepare(row, col, prop, td, value, cellProperties);

    this.#cleanupModal();

    this.#modalContainer = document.createElement('div');
    this.#modalContainer.classList.add('scoring-scale-editor-container');
    document.body.appendChild(this.#modalContainer);

    this.#modalOverlay = document.createElement('div');
    this.#modalOverlay.classList.add('scoring-scale-editor-overlay');
    this.#modalContainer.appendChild(this.#modalOverlay);
  }

  focus(): void {
    // Don't focus the input, we'll show the modal instead
  }

  beginEditing(initialValue?: string): void {
    super.beginEditing(initialValue);
    this.#currentValue = this.originalValue ?? '';
    this.open();
  }

  #cleanupModal(): void {
    if (_notNil(this.#root)) {
      this.#root.unmount();
      this.#root = null;
    }

    if (_notNil(this.#modalContainer) && _notNil(this.#modalContainer.parentNode)) {
      document.body.removeChild(this.#modalContainer);
    }
    this.#modalContainer = null;
    this.#modalOverlay = null;
  }

  close(): void {
    this.#cleanupModal();
    super.close();
  }

  getValue(): string {
    return this.#currentValue;
  }

  setValue(newValue: string): void {
    this.#currentValue = newValue ?? '';
    super.setValue(newValue);
  }

  open(): void {
    super.open();

    this.#currentValue = this.originalValue ?? '';
    const scale = parseScoringScale(this.#currentValue);

    const handleChange = (value: string): void => {
      this.#currentValue = value;
      this.setValue(value);
      this.hot.setDataAtCell(this.row, this.col, value);
      this.finishEditing();
    };

    const handleClose = (): void => {
      this.finishEditing(false, false);
    };

    if (!_notNil(this.#root) && _notNil(this.#modalOverlay)) {
      this.#root = ReactDOM.createRoot(this.#modalOverlay);
    }

    if (_notNil(this.#root)) {
      this.#root.render(
        <StrictMode>
          <ScoringScaleModal defaultValue={scale} td={this.TD} onChange={handleChange} onClose={handleClose} />
        </StrictMode>
      );
    }
  }
}
